import { Injectable } from '@angular/core';
import { catchError, forkJoin, from, map, Observable, of, switchMap } from 'rxjs';
import { BucketService } from '../services/bucket.service';
import { LmsRegistryService } from '../services/lms-registry.service';
import { Tutor, TutorService, TutorTemplate } from '../services/tutor.service';
import { hasCourseKnowledge, hasLinkedFolder, PREVIEW_TRIED_KEY, readKnowledgeMode } from '../components/portal-hints/portal-state';
import { AuthService } from '../services/auth.service';
import { AgentChatService } from '../agent/agent-chat.service';

/** Wie viele Agenten je Prüfung auf ihre Einstellungen gelesen werden
 *  (eine Anfrage je Agent) — wie die Portalhinweise. */
const MODE_CHECK_LIMIT = 3;

export type DoneCheckId =
  | 'has-agent'
  | 'orbi-used'
  | 'preview-tried'
  | 'has-folder'
  | 'agents-have-knowledge'
  | 'knowledge-mode-set'
  | 'course-sync-on'
  | 'has-twin'
  | 'lms-connected'
  | 'agents-placed';

/**
 * Erledigt-Prüfungen der Academy: ob die Person eine Lektion im echten
 * Portal schon umgesetzt hat (PRD academy-content › Prüfen). Gleiche Regeln
 * wie die Portalhinweise (`portal-state.ts`) — was der Hinweis als erledigt
 * ansieht, gilt hier auch. Jede Prüfung fragt frisch und wirft nie: ein
 * Fehler zählt als „noch nicht erledigt“.
 */
@Injectable({ providedIn: 'root' })
export class AcademyDoneChecks {
  constructor(
    private buckets: BucketService,
    private tutors: TutorService,
    private lms: LmsRegistryService,
    private auth: AuthService,
    private agentChat: AgentChatService,
  ) {}

  check(id: DoneCheckId): Observable<boolean> {
    const run: Record<DoneCheckId, () => Observable<boolean>> = {
      'has-agent': () => this.agents().pipe(map(({ all }) => all > 0)),
      'orbi-used': () => this.orbiUsed(),
      'preview-tried': () => of(this.previewTried()),
      'has-folder': () => this.hasFolder(),
      'agents-have-knowledge': () => this.agentsHaveKnowledge(),
      'knowledge-mode-set': () => this.knowledgeModeSet(),
      'course-sync-on': () => this.courseSyncOn(),
      'has-twin': () => this.agents().pipe(map(({ twins }) => twins > 0)),
      'lms-connected': () => this.lms.listRegistrations().pipe(map((regs) => (regs?.length ?? 0) > 0)),
      // Wie Portalhinweis 6: platziert = Agent mit Kurs (`live_course_count`).
      'agents-placed': () => this.agents().pipe(map(({ list }) => list.some((t) => hasCourseKnowledge(t)))),
    };
    return run[id]().pipe(catchError(() => of(false)));
  }

  /** Mindestens ein Gespräch mit Orbi, dem Portal-Assistenten. */
  private orbiUsed(): Observable<boolean> {
    return from(this.agentChat.loadThreads()).pipe(map(() => this.agentChat.threads().length > 0));
  }

  /** Vorschau-Chat schon benutzt — dasselbe Merkmal wie Portalhinweis 4. */
  private previewTried(): boolean {
    const p = this.auth.accessTokenPayload$.value;
    try {
      return localStorage.getItem(`${PREVIEW_TRIED_KEY}:${p?.client_id ?? ''}:${p?.user_id ?? ''}`) === '1';
    } catch {
      return false;
    }
  }

  /** Mindestens ein Wissensbasis-Ordner (Hinweis 1). */
  private hasFolder(): Observable<boolean> {
    return this.buckets.fetchBuckets().pipe(map((list) => list.length > 0));
  }

  /** Jeder Agent außer Alpha Twin hat einen Ordner oder Kurswissen (Hinweis 2). */
  private agentsHaveKnowledge(): Observable<boolean> {
    return this.agents().pipe(
      map(({ agents }) => agents.length > 0 && agents.every((t) => hasLinkedFolder(t) || hasCourseKnowledge(t))),
    );
  }

  /** Kein Agent mit Ordner steht auf „Uneingeschränkt“ (Hinweis 3). */
  private knowledgeModeSet(): Observable<boolean> {
    return this.agents().pipe(
      switchMap(({ agents }) => {
        const linked = agents.filter((t) => hasLinkedFolder(t)).slice(0, MODE_CHECK_LIMIT);
        if (!linked.length) return of(false);
        return forkJoin(
          linked.map((t) =>
            this.tutors.fetchTutorSettings(t.id).pipe(
              map((items) => readKnowledgeMode(items) !== 'open'),
              catchError(() => of(false)),
            ),
          ),
        ).pipe(map((all) => all.every(Boolean)));
      }),
    );
  }

  /** Kurs-Sync an einer LMS-Verbindung oder an einer Platzierung. */
  private courseSyncOn(): Observable<boolean> {
    return this.lms.listRegistrations().pipe(
      switchMap((regs) => {
        if (!regs?.length) return of(false);
        if (regs.some((r) => r.auto_course_sync_enabled)) return of(true);
        return forkJoin(
          regs.map((r) => this.lms.listPlacements(r.id).pipe(catchError(() => of([])))),
        ).pipe(map((lists) => lists.some((l) => l.some((p) => p.course_sync_enabled))));
      }),
    );
  }

  /** Agenten ohne Alpha Twin — der Twin bekommt sein Wissen über den verlinkten Tutor. */
  private agents(): Observable<{ agents: Tutor[]; all: number; twins: number; list: Tutor[] }> {
    return forkJoin([
      this.tutors.listTutors().pipe(map((res: any) => (res?.data ?? []) as Tutor[])),
      this.tutors.listTutorTemplates().pipe(catchError(() => of([] as TutorTemplate[]))),
    ]).pipe(
      map(([list, templates]) => {
        const twin = new Set((templates as TutorTemplate[]).filter((t) => t.name === 'alpha_dozent').map((t) => t.id));
        const isTwin = (t: Tutor) => !!t.template_id && twin.has(t.template_id);
        return { agents: list.filter((t) => !isTwin(t)), all: list.length, twins: list.filter(isTwin).length, list };
      }),
    );
  }
}
